import { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';

import { Redirect } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BorderRadius, Spacing } from '@/constants/spacing';
import { Colors, ModernPalette, Shadows } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFontScale } from '@/hooks/use-font-scale';
import { useSupabaseAuth } from '@/lib/supabase';

type AuthMode = 'signIn' | 'signUp';

const modeCopy: Record<AuthMode, { title: string; subtitle: string; cta: string; helper: string }> = {
  signIn: {
    title: 'Welcome!',
    subtitle: 'Login to FimFam',
    cta: 'Sign in with email',
    helper: "New to FimFam?",
  },
  signUp: {
    title: 'Welcome!',
    subtitle: 'Sign up to FimFam',
    cta: 'Create account',
    helper: 'Already registered?',
  },
};

export default function SignInScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { getScaledTypography } = useFontScale();
  const { width: screenWidth } = useWindowDimensions();

  // Responsive mascot size - 50% of screen width
  const mascotSize = screenWidth * 0.5;

  const { session, loading, signInWithPassword, signUpWithPassword, signInWithGoogle } = useSupabaseAuth();
  const [mode, setMode] = useState<AuthMode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [googlePending, setGooglePending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const typography = useMemo(() => {
    const hero = getScaledTypography('hero');
    const title = getScaledTypography('title');
    const heading = getScaledTypography('heading');
    const subheading = getScaledTypography('subheading');
    const body = getScaledTypography('body');
    const caption = getScaledTypography('caption');

    return {
      hero,
      title,
      subtitle: subheading,
      label: { ...body, fontWeight: '600' as const },
      input: {
        ...body,
      },
      helper: caption,
      error: body,
      feedback: body,
      primaryButtonLabel: { ...body, fontWeight: '600' as const },
      switchText: body,
      dividerText: { ...caption, fontWeight: '600' as const },
      googleIcon: {
        ...heading,
        fontWeight: '700' as const,
      },
      googleButtonLabel: { ...body, fontWeight: '500' as const },
    };
  }, [getScaledTypography]);

  const isSubmitDisabled = useMemo(
    () => pending || !email.trim() || password.length < 6,
    [pending, email, password],
  );

  const toggleMode = useCallback(
    () => setMode(current => (current === 'signIn' ? 'signUp' : 'signIn')),
    [],
  );

  const handleSubmit = useCallback(async () => {
    setPending(true);
    setError(null);
    setFeedback(null);

    const normalizedEmail = email.trim().toLowerCase();

    const action =
      mode === 'signIn'
        ? () => signInWithPassword(normalizedEmail, password)
        : () => signUpWithPassword(normalizedEmail, password);

    const { error: authError, data } = await action();

    if (authError) {
      setError(authError.message);
    } else if (mode === 'signUp') {
      const needsConfirmation = !data.user?.email_confirmed_at;
      setFeedback(
        needsConfirmation
          ? 'Account created. Please check your email to confirm your address before signing in.'
          : 'Account created successfully.',
      );
      setMode('signIn');
    }

    setPending(false);
  }, [email, mode, password, signInWithPassword, signUpWithPassword]);

  const handleGoogleSignIn = useCallback(async () => {
    setGooglePending(true);
    setError(null);
    setFeedback(null);

    const { error: authError } = await signInWithGoogle();

    if (authError) {
      setError(authError.message);
    }

    setGooglePending(false);
  }, [signInWithGoogle]);

  if (session) {
    return <Redirect href="/(tabs)" />;
  }

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: palette.background }]}>
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: palette.background }]}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={24}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Image
              source={require('../assets/images/fim-box-transparent.png')}
              style={{ width: mascotSize, height: mascotSize, marginBottom: 16 }}
              resizeMode="contain"
            />
            <Text style={[typography.hero, { color: palette.text }]}>
              {modeCopy[mode].title}
            </Text>
            <Text style={[typography.subtitle, { color: palette.textMuted }]}>
             {modeCopy[mode].subtitle}
            </Text>
          </View>
          
          <View style={[styles.card, { backgroundColor: palette.cardBackground }, Shadows.md]}>
            <View style={styles.field}>
            <TouchableOpacity
              disabled={googlePending}
              onPress={handleGoogleSignIn}
              style={[
                styles.primaryButton,
                {
                  backgroundColor: googlePending ? `${palette.primary}88` : palette.primary,
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 12,
                },
              ]}>
              {googlePending ? (
                <ActivityIndicator color={ModernPalette.textInverse} />
              ) : (
                <>
                  <Image
                    source={require('../assets/logo/google-logo-small.png')}
                    style={styles.googleLogo}
                    resizeMode="contain"
                  />
                  <Text style={[typography.googleButtonLabel, { color: ModernPalette.textInverse }]}>
                    Sign in with Google
                  </Text>
                </>
              )}
            </TouchableOpacity>
           
           <View style={styles.divider}>
              <View style={[styles.dividerLine, { backgroundColor: palette.borderMuted }]} />
              <Text style={[typography.dividerText, { color: palette.textMuted }]}>
                OR
              </Text>
              <View style={[styles.dividerLine, { backgroundColor: palette.borderMuted }]} />
            </View>
              <Text style={[styles.label, typography.label, { color: palette.textMuted }]}>
                Email
              </Text>
              <TextInput
                value={email}
                onChangeText={setEmail}
                autoCapitalize="none"
                autoComplete="email"
                keyboardType="email-address"
                placeholder="you@email.com"
                placeholderTextColor={palette.textMuted}
                style={[
                  styles.input,
                  typography.input,
                  {
                    color: palette.text,
                    borderColor: palette.borderLight,
                    backgroundColor: '#FFFFFF',
                  },
                ]}
              />
            </View>
            <View style={styles.field}>
              <Text style={[styles.label, typography.label, { color: palette.textMuted }]}>
                Password
              </Text>
              <TextInput
                value={password}
                onChangeText={setPassword}
                secureTextEntry
                placeholder="Type password here"
                placeholderTextColor={palette.textMuted}
                style={[
                  styles.input,
                  typography.input,
                  {
                    color: palette.text,
                    borderColor: palette.borderLight,
                    backgroundColor: '#FFFFFF',
                  },
                ]}
              />
                         </View>

            {error ? (
              <Text style={[typography.error, { color: palette.danger }]}>
                {error}
              </Text>
            ) : null}
            {feedback ? (
              <Text style={[typography.feedback, { color: palette.tint }]}>
                {feedback}
              </Text>
            ) : null}

            <TouchableOpacity
              disabled={isSubmitDisabled}
              onPress={handleSubmit}
              style={[
                styles.primaryButton,
                {
                  backgroundColor: isSubmitDisabled ? `${palette.primary}55` : palette.primary,
                },
              ]}>
              {pending ? (
                <ActivityIndicator color={ModernPalette.textInverse} />
              ) : (
                <Text style={[styles.primaryButtonLabel, typography.primaryButtonLabel]}>
                  {modeCopy[mode].cta}
                </Text>
              )}
            </TouchableOpacity>
                     </View>

          <TouchableOpacity onPress={toggleMode} style={styles.switchMode}>
            <Text style={[typography.switchText, { color: palette.text }]}>
              {modeCopy[mode].helper}{' '}
              <Text style={[typography.switchText, { color: palette.tint }]}>
                {mode === 'signIn' ? 'Create an account' : 'Sign in instead'}
              </Text>
            </Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: Spacing.xxl,
    justifyContent: 'center',
    gap: 28, // intentional non-grid value for visual balance
  },
  header: {
    gap: Spacing.sm,
    alignItems: 'center',
  },
  card: {
    borderRadius: BorderRadius.lg,
    padding: Spacing.xl,
    gap: Spacing.lg,
    overflow: 'hidden',
  },
  field: {
    gap: Spacing.sm,
  },
  label: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  input: {
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingVertical: Spacing.md + 2, // 14px
    paddingHorizontal: Spacing.lg,
  },
  primaryButton: {
    borderRadius: BorderRadius.md,
    paddingVertical: Spacing.lg,
    alignItems: 'center',
  },
  primaryButtonLabel: {
    color: ModernPalette.textInverse,
  },
  switchMode: {
    alignItems: 'center',
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
    marginVertical: Spacing.sm,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  googleLogo: {
    width: 18,
    height: 18,
  },
});
